import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  GraduationCap, RefreshCw, LogOut, Search, AlertTriangle, TrendingUp, TrendingDown,
  Users, Truck, Wallet, Calendar, Building2, Sparkles, ChevronRight, Briefcase, BookOpen,
  Edit3, Send, Check, X, MessageSquare, Heart, AlertCircle, Lightbulb,
  Target, CheckCircle2, XCircle, Clock as ClockIcon, FileText, Loader, MessageSquareHeart,
} from 'lucide-react';
import SaveSlotsBlock from '../components/supervision/SaveSlotsBlock';
import FormateurPhase3Panel from '../components/supervision/FormateurPhase3Panel';
import FormateurRexSection from '../components/supervision/FormateurRexSection';
import ExerciseStatementsBlock from '../components/supervision/ExerciseStatementsBlock';
import { useTranslation } from '../i18n';
import generatePlayerProfilePDF from '../utils/generatePlayerProfilePDF';
import { formatPlayTime } from '../utils/format';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * SupervisionView — pedagogical supervisor (Responsable Pédagogique) dashboard.
 *
 * Accessed via /supervision after validating a master license key on the
 * gate. Reads the master key from localStorage and polls the
 * /api/master/overview/by-key endpoint to display a live cohort overview.
 *
 * The page is intentionally read-only — no actions, no mutation. Just info
 * for the supervisor.
 */
export default function SupervisionView() {
  const { t } = useTranslation();
  const [holder, setHolder] = useState('');
  // 08/02/2026 — milestone email opt-in (toggle in header).
  const [notifyMilestones, setNotifyMilestones] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [savingNotify, setSavingNotify] = useState(false);
  // Boot state — false until /api/master/me has confirmed the session
  // cookie is valid. The license key itself is NEVER stored client-side
  // anymore (lives only in the httpOnly cookie). Components that need to
  // identify "the current master" target the apprentice license key —
  // the supervisor is identified server-side via the cookie.
  const [authReady, setAuthReady] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  // Annotation state.
  const [annotateTarget, setAnnotateTarget] = useState(null); // license_key string
  const [notesHistory, setNotesHistory] = useState({}); // { apprentice_license_key: [notes…] }
  // Objectives state.
  const [objectiveTarget, setObjectiveTarget] = useState(null);
  const [objectivesHistory, setObjectivesHistory] = useState({});
  const [metricsCatalog, setMetricsCatalog] = useState([]);
  // 16/09/2026 — REX de la cohorte (retours d'expérience apprenants).
  const [rexItems, setRexItems] = useState([]);

  // Verify the master session cookie is valid on mount. If not, kick back
  // to the gate. We deliberately don't trust any client-side hint here.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${BACKEND}/api/master/me`, { credentials: 'include' });
        if (!resp.ok) {
          window.location.href = '/';
          return;
        }
        const j = await resp.json();
        setHolder(j.holder_name || '');
        setNotifyMilestones(!!j.notify_milestones);
        setContactEmail(j.contact_email || '');
        // Refresh the cached display name (sessionStorage = cleared on tab close).
        try { sessionStorage.setItem('hst-master-holder', j.holder_name || ''); } catch (_) { /* sessionStorage indispo en mode privé Safari, on ignore */ }
        setAuthReady(true);
      } catch (err) {
        console.warn('[supervision] master session check failed:', err);
        window.location.href = '/';
      }
    })();
  }, []);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(
        `${BACKEND}/api/master/overview/by-key`,
        { credentials: 'include' }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setError(err.detail || 'Erreur de chargement');
        setData(null);
      } else {
        const json = await resp.json();
        setData(json);
      }
    } catch (e) {
      setError(e.message || t('sup_network_error'));
    }
    setLoading(false);
  }, [t]);

  const fetchNotesHistory = useCallback(async () => {
    try {
      const resp = await fetch(
        `${BACKEND}/api/master/notes/history`,
        { credentials: 'include' }
      );
      if (!resp.ok) return;
      const json = await resp.json();
      const grouped = {};
      (json.items || []).forEach(n => {
        grouped[n.apprentice_key] = grouped[n.apprentice_key] || [];
        grouped[n.apprentice_key].push(n);
      });
      setNotesHistory(grouped);
    } catch (err) { console.warn('[supervision] notes history fetch failed:', err); }
  }, []);

  const fetchObjectivesHistory = useCallback(async () => {
    try {
      const resp = await fetch(
        `${BACKEND}/api/master/objectives/history`,
        { credentials: 'include' }
      );
      if (!resp.ok) return;
      const json = await resp.json();
      const grouped = {};
      (json.items || []).forEach(o => {
        grouped[o.apprentice_key] = grouped[o.apprentice_key] || [];
        grouped[o.apprentice_key].push(o);
      });
      setObjectivesHistory(grouped);
    } catch (err) { console.warn('[supervision] objectives history fetch failed:', err); }
  }, []);

  const fetchRex = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND}/api/master/rex`, { credentials: 'include' });
      if (!resp.ok) return;
      const json = await resp.json();
      setRexItems(json.items || []);
    } catch (err) { console.warn('[supervision] rex fetch failed:', err); }
  }, []);

  // Load the metrics catalogue once.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${BACKEND}/api/master/objectives/metrics`);
        if (!resp.ok) return;
        const json = await resp.json();
        setMetricsCatalog(Array.isArray(json?.metrics) ? json.metrics : []);
      } catch (err) { console.warn('[supervision] metrics catalog fetch failed:', err); }
    })();
  }, []);

  useEffect(() => {
    if (authReady) {
      fetchOverview();
      fetchNotesHistory();
      fetchObjectivesHistory();
      fetchRex();
    }
  }, [authReady, fetchOverview, fetchNotesHistory, fetchObjectivesHistory, fetchRex]);

  // Auto-refresh every 60s.
  useEffect(() => {
    if (!authReady) return undefined;
    const id = setInterval(() => {
      fetchOverview();
      fetchNotesHistory();
      fetchObjectivesHistory();
      fetchRex();
    }, 60000);
    return () => clearInterval(id);
  }, [authReady, fetchOverview, fetchNotesHistory, fetchObjectivesHistory, fetchRex]);

  const handleLogout = async () => {
    // Tell the server to clear the session cookie. We don't block on
    // network failures here — the local state is already wiped.
    try {
      await fetch(`${BACKEND}/api/master/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) { console.warn('[supervision] logout request failed:', err); }
    try {
      sessionStorage.removeItem('hst-master-holder');
      // Best-effort: wipe any leftover from previous insecure builds.
      localStorage.removeItem('hst-master-key');
      localStorage.removeItem('hst-master-holder');
    } catch (err) { console.warn('[supervision] logout cleanup failed:', err); }
    window.location.href = '/';
  };

  const items = useMemo(() => {
    const list = data?.items || [];
    if (!filter) return list;
    const f = filter.toLowerCase();
    return list.filter(it =>
      (it.license?.holder_name || '').toLowerCase().includes(f)
      || (it.snapshot?.company_name || '').toLowerCase().includes(f)
    );
  }, [data, filter]);

  // Aggregate stats across the cohort.
  const aggregate = useMemo(() => {
    const items = data?.items || [];
    if (items.length === 0) return null;
    const playing = items.filter(i => i.snapshot && (i.snapshot.total_days || 0) > 0);
    return {
      total: items.length,
      playing: playing.length,
      avgDays: playing.length ? Math.round(playing.reduce((s, i) => s + (i.snapshot.total_days || 0), 0) / playing.length) : 0,
      totalFleet: playing.reduce((s, i) => s + (i.snapshot.fleet_total || 0), 0),
      totalDrivers: playing.reduce((s, i) => s + (i.snapshot.drivers_total || 0), 0),
      negativeEquity: playing.filter(i => (i.snapshot.equity || 0) < 0).length,
    };
  }, [data]);

  // 16/09/2026 — index REX par clé apprenant (badge sur la fiche).
  const rexByKey = useMemo(() => {
    const map = {};
    (rexItems || []).forEach((r) => { map[r.license_key] = r; });
    return map;
  }, [rexItems]);

  return (
    <div className="min-h-screen bg-ink-900 text-slate-100" data-testid="supervision-view">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-900 px-6 py-4 sticky top-0 z-10 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/15 rounded-md">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg text-white">{t('sup_title')}</h1>
              <p className="text-[11px] uppercase tracking-wider font-mono text-white/80">
                {holder || t('sup_supervisor_default')} · {data ? t('sup_apprentices_count', { count: data.children_count }) : t('sup_loading')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ToggleNotifyMilestones
              checked={notifyMilestones}
              saving={savingNotify}
              hasEmail={!!contactEmail}
              onChange={async (next) => {
                setSavingNotify(true);
                try {
                  const r = await fetch(`${BACKEND}/api/master/notify-milestones`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ enabled: next }),
                  });
                  if (r.ok) {
                    const j = await r.json();
                    setNotifyMilestones(!!j.notify_milestones);
                  }
                } finally { setSavingNotify(false); }
              }}
            />
            <button
              onClick={() => fetchOverview()}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-40"
              data-testid="supervision-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('sup_refresh_btn')}
            </button>
            <button
              onClick={handleLogout}
              className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors flex items-center gap-1.5"
              data-testid="supervision-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              {t('sup_logout_btn')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-4">
        {error && (
          <div className="rounded-md bg-danger/10 border border-danger/40 px-4 py-3 flex items-center gap-2" data-testid="supervision-error">
            <AlertTriangle className="w-4 h-4 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {/* Aggregate summary */}
        {aggregate && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="supervision-summary">
            <SummaryTile testId="agg-total"      label={t('sup_agg_apprentices')} value={aggregate.total} icon={Users} color="slate" />
            <SummaryTile testId="agg-playing"    label={t('sup_agg_playing')} value={aggregate.playing} icon={Sparkles} color="money" />
            <SummaryTile testId="agg-avg-days"   label={t('sup_agg_avg_days')} value={aggregate.avgDays} icon={Calendar} color="slate" />
            <SummaryTile testId="agg-total-fleet" label={t('sup_agg_total_fleet')} value={aggregate.totalFleet} icon={Truck} color="slate" />
            <SummaryTile testId="agg-neg-equity" label={t('sup_agg_neg_equity')} value={aggregate.negativeEquity} icon={AlertTriangle} color={aggregate.negativeEquity > 0 ? 'danger' : 'money'} />
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('sup_search_placeholder')}
              className="w-full bg-ink-800 border border-ink-600 rounded-md pl-9 pr-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="supervision-search"
            />
          </div>
          <span className="text-xs text-slate-500">{t('sup_displayed_count', { count: items.length })}</span>
        </div>

        {/* 11/02/2026 (Phase 3 — Superpouvoirs formateur) */}
        <FormateurPhase3Accordion apprentices={(data?.items || []).map((it) => it.license)} />
        {/* 16/09/2026 — REX de la cohorte */}
        <FormateurRexSection items={rexItems} />
        {/* Cohort list */}
        {loading && !data ? (
          <div className="text-center py-16 text-slate-500" data-testid="supervision-loading">{t('sup_loading')}</div>
        ) : items.length === 0 ? (
          <div className="rounded-md bg-ink-800 border border-ink-600 px-4 py-8 text-center" data-testid="supervision-empty">
            <Users className="w-8 h-8 text-slate-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">{t('sup_empty_cohort')}</p>
            <p className="text-[11px] text-slate-500 mt-1">
              {t('sup_empty_hint')}
            </p>
          </div>
        ) : (
          <div className="ph-no-capture space-y-3" data-testid="supervision-cohort">
            {items.map((it) => (
              <LearnerCard
                key={it.license.license_key}
                item={it}
                notes={notesHistory[it.license.license_key] || []}
                objectives={objectivesHistory[it.license.license_key] || []}
                rex={rexByKey[it.license.license_key] || null}
                onAnnotate={() => setAnnotateTarget(it.license.license_key)}
                onObjective={() => setObjectiveTarget(it.license.license_key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Annotate modal */}
      {annotateTarget && (
        <AnnotateModal
          apprenticeKey={annotateTarget}
          apprenticeName={(data?.items || []).find(i => i.license.license_key === annotateTarget)?.license?.holder_name || ''}
          existingNotes={notesHistory[annotateTarget] || []}
          onClose={() => setAnnotateTarget(null)}
          onSent={() => fetchNotesHistory()}
        />
      )}

      {/* Objective modal */}
      {objectiveTarget && (
        <ObjectiveModal
          apprenticeKey={objectiveTarget}
          apprenticeName={(data?.items || []).find(i => i.license.license_key === objectiveTarget)?.license?.holder_name || ''}
          metricsCatalog={metricsCatalog}
          existingObjectives={objectivesHistory[objectiveTarget] || []}
          onClose={() => setObjectiveTarget(null)}
          onChanged={() => fetchObjectivesHistory()}
        />
      )}
    </div>
  );
}

// ============================================================================
// === Learner Card ===
// ============================================================================

const LearnerCard = ({ item, notes, objectives, rex, onAnnotate, onObjective }) => {
  const { t } = useTranslation();
  const { license, snapshot } = item;
  const playing = !!snapshot && (snapshot.total_days || 0) > 0;
  const notesCount = (notes || []).length;
  const unreadByApprentice = (notes || []).filter(n => !n.ack_at).length;
  const activeObjectives = (objectives || []).filter(o => o.status === 'active').length;
  const achievedObjectives = (objectives || []).filter(o => o.status === 'achieved').length;
  const failedObjectives = (objectives || []).filter(o => o.status === 'failed').length;

  // 07/02/2026 — P1 retour user : PDF apprenant côté superviseur (idem
  // super-vue admin). Le PDF est généré client-side puis téléchargé pour
  // le formateur. Pas de roundtrip backend ici (le master n'a pas de
  // route player-message ; le formateur peut l'envoyer manuellement par
  // email s'il en a besoin).
  const [pdfState, setPdfState] = useState('idle'); // idle | generating | error
  // 28/07/2026 — Fiche par partie : si plusieurs parties sont connues du
  // serveur, le formateur choisit laquelle éditer.
  const [runChooser, setRunChooser] = useState(null);
  const generateFicheFor = (snap) => {
    try {
      const slug = (license.holder_name || snap.company_name || 'apprenant')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      generatePlayerProfilePDF({
        snapshot: snap,
        license,
        download: true,
        filename: `hst-fiche-${slug}.pdf`,
      });
      setPdfState('idle');
      setRunChooser(null);
    } catch (err) {
      console.error('[supervision] PDF export failed', err);
      setPdfState('error');
      setTimeout(() => setPdfState('idle'), 3000);
    }
  };
  const handleExportPDF = async (e) => {
    e.stopPropagation();
    if (!snapshot) return;
    setPdfState('generating');
    let runs = [];
    try {
      const resp = await fetch(
        `${BACKEND}/api/telemetry/game-runs?license_key=${encodeURIComponent(license.license_key)}`,
        { credentials: 'include' },
      );
      if (resp.ok) runs = (await resp.json()).runs || [];
    } catch (_) { /* fallback : dernier snapshot connu */ }
    if (runs.length > 1) {
      setRunChooser(runs);
      setPdfState('idle');
      return;
    }
    generateFicheFor(runs[0] || snapshot);
  };

  return (
    <div
      className="rounded-lg border border-ink-600 bg-ink-800 overflow-hidden"
      data-testid={`learner-${license.license_key}`}
    >
      <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
            <span className="text-purple-300 font-bold text-sm">
              {(license.holder_name || '?').slice(0, 1).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-100">{license.holder_name || '—'}</p>
            <p className="text-[10px] text-slate-500 font-mono">{license.license_key}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rex && (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/40 font-bold uppercase tracking-wider flex items-center gap-1"
              title={t('sup_rex_badge_title', { date: new Date(rex.created_at).toLocaleDateString('fr-FR') })}
              data-testid={`rex-badge-${license.license_key}`}
            >
              <MessageSquareHeart className="w-3 h-3" /> {t('sup_rex_badge')}
            </span>
          )}
          {license.bankruptcy ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-mono uppercase tracking-wider border border-rose-500/40 font-bold" data-testid={`bankruptcy-badge-${license.license_key}`}>
              {t('sup_liquidated_on', { day: license.bankruptcy.liquidation_day })}
            </span>
          ) : !playing ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 font-mono uppercase tracking-wider">
              {t('sup_not_played_yet')}
            </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/10 text-money font-mono uppercase tracking-wider">
              J{snapshot.total_days} · {snapshot.company_name || '—'}
            </span>
          )}
          <button
            onClick={onObjective}
            className="flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 border border-sky-500/30 font-semibold uppercase tracking-wider transition-colors"
            data-testid={`objective-btn-${license.license_key}`}
          >
            <Target className="w-3 h-3" />
            {t('sup_objective_btn')}
            {(activeObjectives + achievedObjectives + failedObjectives) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-sky-500/30 text-white text-[9px] font-mono">
                {activeObjectives > 0 ? t('sup_active_count', { count: activeObjectives }) : ''}
                {achievedObjectives > 0 ? ` · ${achievedObjectives}✓` : ''}
                {failedObjectives > 0 ? ` · ${failedObjectives}✗` : ''}
              </span>
            )}
          </button>
          <button
            onClick={onAnnotate}
            className="flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 border border-purple-500/30 font-semibold uppercase tracking-wider transition-colors"
            data-testid={`annotate-btn-${license.license_key}`}
          >
            <Edit3 className="w-3 h-3" />
            {t('sup_annotate_btn')}
            {notesCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-purple-500/30 text-white text-[9px] font-mono">
                {notesCount}{unreadByApprentice > 0 ? ` · ${unreadByApprentice} ${t('sup_unread_short')}` : ''}
              </span>
            )}
          </button>
          {/* PDF apprenant — 07/02/2026 (parité avec super-vue admin) */}
          <button
            onClick={handleExportPDF}
            disabled={!playing || pdfState === 'generating'}
            title={playing ? t('sup_pdf_title_active') : t('sup_pdf_title_inactive')}
            className={`flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider transition-colors border ${
              !playing
                ? 'bg-slate-700/40 text-slate-500 border-slate-700 cursor-not-allowed'
                : pdfState === 'error'
                  ? 'bg-rose-500/15 text-rose-300 border-rose-500/40'
                  : 'bg-amber-500/15 text-amber-200 border-amber-500/30 hover:bg-amber-500/25'
            }`}
            data-testid={`pdf-btn-${license.license_key}`}
          >
            {pdfState === 'generating'
              ? <Loader className="w-3 h-3 animate-spin" />
              : <FileText className="w-3 h-3" />}
            {pdfState === 'error' ? t('sup_pdf_btn_error') : t('sup_pdf_btn')}
          </button>
        </div>
      </div>
      {/* 28/07/2026 — Fiche par partie : sélecteur quand plusieurs parties */}
      {runChooser && (
        <div className="px-4 py-3 border-b border-ink-600 bg-ink-700/30" data-testid={`run-chooser-${license.license_key}`}>
          <p className="text-[11px] text-slate-300 font-semibold mb-2">
            Plusieurs parties détectées — choisissez celle à éditer en fiche :
          </p>
          <div className="space-y-1.5">
            {runChooser.map((r, i) => (
              <button
                key={r.game_run_id || i}
                onClick={() => generateFicheFor(r)}
                data-testid={`run-option-${license.license_key}-${i}`}
                className="w-full flex items-center justify-between text-left text-[11px] px-3 py-2 rounded-md bg-ink-800 border border-ink-600 hover:border-amber-500/40 transition-colors"
              >
                <span className="text-slate-200 font-semibold">
                  {r.company_name || 'Partie'}
                  <span className="text-slate-500 font-normal">
                    {/* i18n-keep */ ' · Difficulté '}{({ facile: 'Facile', normal: 'Normal', difficile: 'Difficile' })[r.difficulty] || r.difficulty || 'Normal'}
                  </span>
                </span>
                <span className="font-mono text-slate-400">J{r.total_days || 0} · An {r.year || 1}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setRunChooser(null)}
            data-testid={`run-chooser-cancel-${license.license_key}`}
            className="mt-2 text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
          >
            Annuler
          </button>
        </div>
      )}

      {playing ? (
        <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`learner-detail-${license.license_key}`}>
          <Metric icon={Wallet}    label={t('sup_metric_treasury')}    value={fmtEur(snapshot.treasury)}   color={snapshot.treasury < 5000 ? 'danger' : 'money'} testId={`m-treasury-${license.license_key}`} />
          <Metric icon={TrendingUp} label={t('sup_metric_equity')} value={fmtEur(snapshot.equity)} color={snapshot.equity < 0 ? 'danger' : 'money'} testId={`m-equity-${license.license_key}`} />
          <Metric icon={TrendingUp} label={t('sup_metric_monthly_ca')}   value={fmtEur(snapshot.monthly_revenue)} color="slate" testId={`m-revenue-${license.license_key}`} />
          <Metric icon={TrendingDown} label={t('sup_metric_monthly_charges')} value={fmtEur(snapshot.monthly_expenses)} color="slate" testId={`m-expenses-${license.license_key}`} />

          <Metric icon={Truck} label={t('sup_metric_fleet')} value={t('sup_fleet_total_active', { total: snapshot.fleet_total, active: snapshot.fleet_active })} color="slate" testId={`m-fleet-${license.license_key}`} />
          <Metric icon={Users} label={t('sup_metric_drivers')} value={t('sup_drivers_satisfaction', { count: snapshot.drivers_total, sat: snapshot.drivers_avg_satisfaction })} color="slate" testId={`m-drivers-${license.license_key}`} />
          <Metric icon={Briefcase} label={t('sup_metric_support')} value={snapshot.support_total} color="slate" testId={`m-support-${license.license_key}`} />
          <Metric icon={Building2} label={t('sup_metric_depot')} value={t('sup_phase_label', { phase: snapshot.depot_phase })} color="slate" testId={`m-depot-${license.license_key}`} />

          <Metric icon={Sparkles}   label={t('reputation_short')}   value={snapshot.reputation} color="slate" testId={`m-rep-${license.license_key}`} />
          <Metric icon={Sparkles}   label={t('notoriety_short')}    value={snapshot.notoriety} color="slate" testId={`m-noto-${license.license_key}`} />
          <Metric icon={BookOpen}   label={t('sup_metric_active_contracts')} value={snapshot.contracts_active} color="slate" testId={`m-contracts-${license.license_key}`} />
          <Metric icon={Calendar}   label={t('sup_metric_game_phase')} value={snapshot.phase} color="slate" testId={`m-phase-${license.license_key}`} />
          <Metric icon={ClockIcon}  label={t('sup_metric_playtime')} value={formatPlayTime(snapshot.play_time_seconds)} color="slate" testId={`m-playtime-${license.license_key}`} />

          {/* Strategy summary */}
          {snapshot.strategy_summary && snapshot.strategy_summary.length > 0 && (
            <div className="col-span-2 lg:col-span-4 rounded-md bg-ink-700/40 border border-ink-600 p-3 mt-1" data-testid={`strategy-${license.license_key}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5 flex items-center gap-1">
                <ChevronRight className="w-3 h-3" /> {t('sup_strategic_analysis')}
              </p>
              <ul className="space-y-0.5 text-[11px] text-slate-300">
                {snapshot.strategy_summary.map((s) => (
                  <li key={`strat-${s}`} className="leading-relaxed">{s}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Client-side save slots (3 max) */}
          <div className="col-span-2 lg:col-span-4 rounded-md bg-ink-700/40 border border-ink-600 p-3 mt-1">
            <SaveSlotsBlock
              saveSlots={snapshot.save_slots}
              testIdPrefix={`save-slots-${license.license_key}`}
            />
          </div>

          {/* 25/07/2026 — Débriefs annuels (demande user : le responsable
              pédagogique voit les clôtures d'exercice + percentiles). */}
          <div className="col-span-2 lg:col-span-4 rounded-md bg-ink-700/40 border border-ink-600 p-3 mt-1">
            <YearDebriefsBlock licenseKey={license.license_key} />
          </div>

          {/* 21/09/2026 — Bilans d'exercice (clos + cumulé) depuis la télémétrie */}
          <div className="col-span-2 lg:col-span-4 rounded-md bg-ink-700/40 border border-ink-600 p-3 mt-1">
            <ExerciseStatementsBlock snapshot={snapshot} license={license} />
          </div>
        </div>
      ) : (
        <div className="px-4 py-6 text-center" data-testid={`no-data-${license.license_key}`}>
          <p className="text-[11px] text-slate-500">
            {t('sup_not_started_p1')}
            {' '}
            {t('sup_not_started_p2')}
          </p>
        </div>
      )}
    </div>
  );
};

// ── 25/07/2026 — Débriefs annuels de l'apprenant (demande user) ──
// Liste des clôtures d'exercice remontées par le jeu (télémétrie
// /telemetry/year-debrief) + percentiles vs la cohorte de la même année.
const YearDebriefsBlock = ({ licenseKey }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [pcts, setPcts] = useState({});

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const resp = await fetch(`${BACKEND}/api/telemetry/year-debrief/by-license?license_key=${encodeURIComponent(licenseKey)}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (!alive) return;
        const rows = data.items || [];
        setItems(rows);
        const results = await Promise.all(rows.map(async (r) => {
          try {
            const p = await fetch(`${BACKEND}/api/telemetry/year-debrief/percentiles?license_key=${encodeURIComponent(licenseKey)}&year=${r.year}`);
            if (!p.ok) return [r.year, null];
            const pd = await p.json();
            return [r.year, pd];
          } catch (_) { return [r.year, null]; }
        }));
        if (alive) setPcts(Object.fromEntries(results));
      } catch (err) {
        console.warn('[supervision] year debriefs fetch failed:', err);
        if (alive) setItems([]);
      }
    };
    run();
    return () => { alive = false; };
  }, [licenseKey]);

  const fmtEurShort = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
  const pctTone = (p) => p >= 70 ? 'text-emerald-300' : p >= 40 ? 'text-amber-300' : 'text-slate-400';
  const PCT_ROWS = [
    ['treasury', t('treasury')], ['year_profit', t('sup_yd_profit')], ['fleet_total', t('nav_fleet')],
    ['reputation', t('reputation')], ['pace', t('sup_yd_pace')],
  ];

  return (
    <div data-testid={`year-debriefs-${licenseKey}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5 flex items-center gap-1">
        <Calendar className="w-3 h-3" /> {t('sup_yd_title')}
      </p>
      {items === null && <p className="text-[10px] text-slate-500 italic">{t('sup_yd_loading')}</p>}
      {items !== null && items.length === 0 && (
        <p className="text-[10px] text-slate-500 italic" data-testid={`year-debriefs-empty-${licenseKey}`}>
          {t('sup_yd_empty')}
        </p>
      )}
      {(items || []).map((r) => {
        const pd = pcts[r.year];
        const pp = pd && pd.ok ? pd.percentiles : null;
        return (
          <div key={`yd-${r.year}`} className="border-t border-ink-600/60 py-1.5 first:border-0" data-testid={`year-debrief-row-${licenseKey}-${r.year}`}>
            <div className="flex items-center justify-between text-[11px]">
              <p className="font-bold text-slate-200">
                {t('sup_yd_year_phase', { year: r.year })}{' '}
                <span className="text-slate-500 font-normal">— {t('sup_yd_phase', { phase: r.phase })}</span>
              </p>
              <p className="font-mono text-slate-300">
                {t('sup_yd_revenue_short')} {fmtEurShort(r.year_revenue)}
                <span className={`ml-2 ${r.year_profit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {r.year_profit >= 0 ? '+' : ''}{fmtEurShort(r.year_profit)}
                </span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-0.5">
              <span>{t('treasury')} {fmtEurShort(r.treasury)}</span>
              <span>{t('sup_yd_vehicles', { n: r.fleet_total })}</span>
              <span>{t('sup_yd_drivers', { n: r.drivers_total })}</span>
              <span>{t('sup_yd_ao_won', { n: r.ao_won })}</span>
              <span>{formatPlayTime(r.play_time_seconds)}</span>
            </div>
            {pp && (pd.sample_size || 0) >= 4 ? (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] mt-0.5">
                {PCT_ROWS.map(([key, label]) => (pp[key] === null || pp[key] === undefined) ? null : (
                  <span key={key} className="text-slate-500">
                    {label} : <span className={`font-bold ${pctTone(pp[key])}`}>
                      {key === 'pace' ? t('sup_yd_faster_than', { p: pp[key] }) : t('sup_yd_better_than', { p: pp[key] })}
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[9px] text-slate-600 italic mt-0.5">
                {t('sup_yd_pct_pending')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TONE_OPTIONS = [
  { id: 'encouragement', labelKey: 'sup_tone_encouragement', icon: Heart,      ring: 'border-emerald-400 bg-emerald-500/15 text-emerald-300', dot: 'bg-emerald-400' },
  { id: 'advice',        labelKey: 'sup_tone_advice',        icon: Lightbulb,  ring: 'border-sky-400 bg-sky-500/15 text-sky-300',             dot: 'bg-sky-400' },
  { id: 'warning',       labelKey: 'sup_tone_warning',       icon: AlertCircle,ring: 'border-amber-400 bg-amber-500/15 text-amber-300',       dot: 'bg-amber-400' },
];

const TONE_BY_ID = TONE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.id]: opt }), {});

const AnnotateModal = ({ apprenticeKey, apprenticeName, existingNotes, onClose, onSent }) => {
  const { t } = useTranslation();
  const [tone, setTone] = useState('encouragement');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const maxLen = 600;
  const remaining = maxLen - body.length;

  const handleSend = async () => {
    const text = body.trim();
    if (!text) { setError(t('sup_msg_empty')); return; }
    setSending(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND}/api/master/notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apprentice_license_key: apprenticeKey,
          body: text,
          tone,
        }),
      });
      if (!resp.ok) {
        let detail = '';
        try { const j = await resp.json(); detail = j.detail || ''; } catch (_) { /* body non JSON (502 nginx etc.), on garde detail vide */ }
        setError(detail || t('sup_send_error'));
      } else {
        setSuccess(true);
        setBody('');
        onSent && onSent();
        setTimeout(() => setSuccess(false), 1600);
      }
    } catch (e) {
      setError(e.message || t('sup_network_error'));
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="annotate-modal-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto border border-purple-500/30"
        onClick={(e) => e.stopPropagation()}
        data-testid="annotate-modal"
      >
        <div className="px-5 py-4 border-b border-ink-600 bg-gradient-to-r from-purple-700/30 to-purple-900/30 flex items-center justify-between sticky top-0 z-10 backdrop-blur">
          <div>
            <h3 className="font-bold text-slate-100 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-300" /> {t('sup_annotate_title', { name: apprenticeName })}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {t('sup_annotate_subtitle')}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-ink-700/60 rounded" data-testid="annotate-close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Tone picker */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-2 block uppercase tracking-wider">{t('sup_tonality_label')}</label>
            <div className="grid grid-cols-3 gap-2">
              {TONE_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const selected = tone === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTone(opt.id)}
                    className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                      selected ? opt.ring : 'bg-background border-ink-600 text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid={`tone-${opt.id}`}
                  >
                    <Icon className="w-3.5 h-3.5 inline mr-1" /> {t(opt.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_message_label')}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, maxLen))}
              rows={4}
              placeholder={t('sup_message_placeholder')}
              className="w-full bg-background text-slate-100 placeholder-slate-600 border border-ink-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              data-testid="annotate-textarea"
            />
            <p className={`text-[10px] text-right mt-1 font-mono ${remaining < 60 ? 'text-amber-400' : 'text-slate-500'}`}>
              {t('sup_chars_remaining', { count: remaining, plural: remaining !== 1 ? 's' : '' })}
            </p>
          </div>

          {error && <p className="text-xs text-danger bg-danger/10 rounded p-2" data-testid="annotate-error">{error}</p>}
          {success && (
            <p className="text-xs text-money bg-money/10 rounded p-2 flex items-center gap-1.5" data-testid="annotate-success">
              <Check className="w-3.5 h-3.5" /> {t('sup_annotation_sent')}
            </p>
          )}

          <button
            onClick={handleSend}
            disabled={sending || !body.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-lg font-bold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid="annotate-send"
          >
            <Send className="w-4 h-4" />
            {sending ? t('sup_sending') : t('sup_send_annotation_btn')}
          </button>

          {/* History */}
          {existingNotes && existingNotes.length > 0 && (
            <div className="border-t border-ink-600 pt-3 mt-3" data-testid="annotate-history">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">
                {t('sup_history_label')} ({existingNotes.length})
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {existingNotes.map(n => {
                  const tone = TONE_BY_ID[n.tone] || TONE_BY_ID.encouragement;
                  const Icon = tone.icon;
                  return (
                    <div key={n.id} className="rounded-md bg-ink-700/40 border border-ink-600 p-2.5" data-testid={`history-note-${n.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${tone.ring} border`}>
                          <Icon className="w-2.5 h-2.5" /> {t(tone.labelKey)}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {new Date(n.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {n.ack_at && <Check className="w-3 h-3 inline ml-1 text-money" />}
                        </span>
                      </div>
                      <p className="text-xs text-slate-200 leading-relaxed">{n.body}</p>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {n.ack_at ? t('sup_read_by_apprentice') : t('sup_awaiting_read')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OBJ_STATUS_STYLE = {
  active:    { labelKey: 'sup_obj_status_active',    icon: ClockIcon,    ring: 'border-sky-400 bg-sky-500/15 text-sky-300' },
  achieved:  { labelKey: 'sup_obj_status_achieved',  icon: CheckCircle2, ring: 'border-emerald-400 bg-emerald-500/15 text-emerald-300' },
  failed:    { labelKey: 'sup_obj_status_failed',    icon: XCircle,      ring: 'border-amber-400 bg-amber-500/15 text-amber-300' },
  cancelled: { labelKey: 'sup_obj_status_cancelled', icon: X,            ring: 'border-slate-500 bg-slate-700/40 text-slate-400' },
};

const ObjectiveModal = ({ apprenticeKey, apprenticeName, metricsCatalog, existingObjectives, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState(metricsCatalog?.[0]?.id || 'treasury');
  const [operator, setOperator] = useState('>=');
  const [threshold, setThreshold] = useState('');
  const [deadlineDay, setDeadlineDay] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Reset metric default when catalog arrives.
  useEffect(() => {
    const firstId = metricsCatalog?.[0]?.id;
    if (firstId) setMetric(prev => prev || firstId);
  }, [metricsCatalog]);

  const handleCreate = async () => {
    const titleClean = title.trim();
    const v = Number(threshold);
    if (!titleClean) { setError(t('sup_obj_title_required')); return; }
    if (Number.isNaN(v)) { setError(t('sup_obj_threshold_invalid')); return; }
    const dl = deadlineDay === '' ? null : Number(deadlineDay);
    if (dl !== null && (Number.isNaN(dl) || dl < 1)) {
      setError(t('sup_obj_deadline_invalid')); return;
    }
    setSending(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND}/api/master/objectives`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apprentice_license_key: apprenticeKey,
          title: titleClean,
          metric, operator,
          threshold: v,
          deadline_day: dl,
        }),
      });
      if (!resp.ok) {
        let detail = '';
        try { const j = await resp.json(); detail = j.detail || ''; } catch (_) { /* body non JSON, on garde detail vide */ }
        setError(detail || t('sup_obj_create_error'));
      } else {
        setSuccess(true);
        setTitle(''); setThreshold(''); setDeadlineDay('');
        onChanged && onChanged();
        setTimeout(() => setSuccess(false), 1600);
      }
    } catch (e) {
      setError(e.message || t('sup_network_error'));
    }
    setSending(false);
  };

  const handleCancel = async (objId) => {
    if (!window.confirm(t('sup_obj_confirm_cancel'))) return;
    try {
      const resp = await fetch(
        `${BACKEND}/api/master/objectives/${encodeURIComponent(objId)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (resp.ok) onChanged && onChanged();
    } catch (_) { /* silent */ }
  };

  const sortedHistory = [...(existingObjectives || [])].sort((a, b) => {
    const order = { active: 0, achieved: 1, failed: 2, cancelled: 3 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="objective-modal-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto border border-sky-500/30"
        onClick={(e) => e.stopPropagation()}
        data-testid="objective-modal"
      >
        <div className="px-5 py-4 border-b border-ink-600 bg-gradient-to-r from-sky-700/30 to-sky-900/30 flex items-center justify-between sticky top-0 z-10 backdrop-blur">
          <div>
            <h3 className="font-bold text-slate-100 flex items-center gap-2">
              <Target className="w-4 h-4 text-sky-300" /> {t('sup_obj_modal_title', { name: apprenticeName })}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {t('sup_obj_subtitle')}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-ink-700/60 rounded" data-testid="objective-close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Title */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_obj_title_label')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 120))}
              placeholder={t('sup_obj_title_placeholder')}
              className="w-full bg-background text-slate-100 placeholder-slate-600 border border-ink-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid="objective-title"
            />
          </div>

          {/* Metric / operator / threshold */}
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-6">
              <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_obj_metric_label')}</label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="w-full bg-background text-slate-100 border border-ink-600 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="objective-metric"
              >
                {(metricsCatalog || []).map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_obj_op_label')}</label>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                className="w-full bg-background text-slate-100 border border-ink-600 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="objective-operator"
              >
                <option value=">=">≥</option>
                <option value="<=">≤</option>
              </select>
            </div>
            <div className="col-span-4">
              <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_obj_threshold_label')}</label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="30000"
                className="w-full bg-background text-slate-100 placeholder-slate-600 border border-ink-600 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="objective-threshold"
              />
            </div>
          </div>

          {/* Deadline */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block uppercase tracking-wider">{t('sup_obj_deadline_label')}</label>
            <input
              type="number"
              min={1}
              value={deadlineDay}
              onChange={(e) => setDeadlineDay(e.target.value)}
              placeholder="90"
              className="w-full bg-background text-slate-100 placeholder-slate-600 border border-ink-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid="objective-deadline"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              {t('sup_obj_deadline_hint')}
            </p>
          </div>

          {error && <p className="text-xs text-danger bg-danger/10 rounded p-2" data-testid="objective-error">{error}</p>}
          {success && (
            <p className="text-xs text-money bg-money/10 rounded p-2 flex items-center gap-1.5" data-testid="objective-success">
              <Check className="w-3.5 h-3.5" /> {t('sup_obj_created_success')}
            </p>
          )}

          <button
            onClick={handleCreate}
            disabled={sending || !title.trim() || threshold === ''}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2.5 rounded-lg font-bold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid="objective-create"
          >
            <Target className="w-4 h-4" />
            {sending ? t('sup_obj_creating') : t('sup_obj_create_btn')}
          </button>

          {/* History */}
          {sortedHistory.length > 0 && (
            <div className="border-t border-ink-600 pt-3 mt-3" data-testid="objective-history">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">
                {t('sup_history_label')} ({sortedHistory.length})
              </p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {sortedHistory.map(o => {
                  const s = OBJ_STATUS_STYLE[o.status] || OBJ_STATUS_STYLE.active;
                  const Icon = s.icon;
                  return (
                    <div key={o.id} className="rounded-md bg-ink-700/40 border border-ink-600 p-2.5" data-testid={`history-obj-${o.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${s.ring} border`}>
                          <Icon className="w-2.5 h-2.5" /> {t(s.labelKey)}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {new Date(o.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-200 leading-relaxed font-semibold">{o.title}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                        {o.metric_label || o.metric} {o.operator} {Number(o.threshold).toLocaleString('fr-FR')}
                        {o.deadline_day != null && <span> · {t('sup_obj_deadline_short', { day: o.deadline_day })}</span>}
                      </p>
                      {o.status === 'achieved' && o.achieved_at && (
                        <p className="text-[10px] text-emerald-400 mt-0.5">
                          {t('sup_obj_achieved_on', { date: new Date(o.achieved_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) })}
                          {o.achieved_value != null && <span> · {t('sup_obj_value_label', { value: Number(o.achieved_value).toLocaleString('fr-FR') })}</span>}
                        </p>
                      )}
                      {o.status === 'failed' && o.failed_at && (
                        <p className="text-[10px] text-amber-400 mt-0.5">
                          {t('sup_obj_failed_on', { date: new Date(o.failed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) })}
                        </p>
                      )}
                      {o.status === 'active' && (
                        <div className="flex justify-end mt-1">
                          <button
                            onClick={() => handleCancel(o.id)}
                            className="text-[10px] px-2 py-0.5 rounded bg-warn/10 text-warn hover:bg-warn/20 transition-colors"
                            data-testid={`cancel-obj-${o.id}`}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ testId, label, value, icon: Icon, color }) => {
  const colorMap = {
    money: 'text-money border-money/30 bg-money/5',
    danger: 'text-danger border-danger/30 bg-danger/5',
    slate: 'text-slate-200 border-ink-600 bg-ink-800',
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || colorMap.slate}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider opacity-80 font-mono">{label}</p>
      </div>
      <p className="text-2xl font-black font-mono">{value}</p>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color, testId }) => (
  <div className="flex items-start gap-2 rounded-md bg-ink-700/30 border border-ink-600 p-2" data-testid={testId}>
    <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <p className="text-[9px] text-slate-500 uppercase tracking-wider font-mono">{label}</p>
      <p className={`text-sm font-bold font-mono ${color === 'danger' ? 'text-danger' : color === 'money' ? 'text-money' : 'text-slate-100'}`}>
        {value}
      </p>
    </div>
  </div>
);

const fmtEur = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Math.round(n);
  return v.toLocaleString('fr-FR') + ' €';
};

/**
 * 08/02/2026 — ToggleNotifyMilestones : opt-in du formateur pour recevoir
 * un mail dès qu'un apprenant franchit une phase. Stocké côté master
 * license. Désactivé tant que l'adresse contact_email n'est pas renseignée
 * (sinon l'email ne pourrait pas partir).
 */
function ToggleNotifyMilestones({ checked, saving, hasEmail, onChange }) {
  const { t } = useTranslation();
  const disabled = !hasEmail || saving;
  const title = !hasEmail
    ? t('sup_notify_milestones_no_email')
    : checked
      ? t('sup_notify_milestones_active_tip')
      : t('sup_notify_milestones_off_tip');
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      title={title}
      className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors flex items-center gap-1.5 ${
        checked
          ? 'bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 border border-emerald-400/40'
          : 'bg-white/10 text-white hover:bg-white/20'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      data-testid="supervision-notify-milestones-toggle"
      aria-pressed={checked}
    >
      <span className={`w-2 h-2 rounded-full ${checked ? 'bg-emerald-300' : 'bg-slate-400'}`} />
      {t('sup_notify_milestones_label', { state: checked ? 'ON' : 'OFF' })}
    </button>
  );
}

// 11/02/2026 (Phase 3) — Accordéon pour le panneau formateur (cohortes /
// broadcast / scénarios). Replié par défaut pour ne pas dominer le
// dashboard, expand pour révéler les 3 sections.
// Badge "Nouveau" affiché tant que le formateur n'a pas expand une première
// fois (persistance localStorage).
const PHASE3_SEEN_LS_KEY = 'hst_phase3_seen_v1';

function FormateurPhase3Accordion({ apprentices }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(PHASE3_SEEN_LS_KEY) === '1'; } catch (_) { return true; }
  });
  const showBadge = !seen;
  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && !seen) {
        setSeen(true);
        try { localStorage.setItem(PHASE3_SEEN_LS_KEY, '1'); } catch (_) { /* silent */ }
      }
      return next;
    });
  };
  return (
    <section className="bg-purple-900/20 border border-purple-500/40 rounded-lg overflow-hidden" data-testid="formateur-phase3-accordion">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-900/30 transition-colors"
        data-testid="formateur-phase3-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles className={`w-4 h-4 text-purple-300 ${showBadge ? 'animate-pulse' : ''}`} />
          <span className="text-sm font-bold text-purple-100 uppercase tracking-wider">
            Superpouvoirs formateur
          </span>
          {showBadge && (
            <span
              className="text-[9px] font-bold uppercase tracking-widest bg-amber-400 text-black px-1.5 py-0.5 rounded animate-pulse"
              data-testid="formateur-phase3-new-badge"
            >
              Nouveau
            </span>
          )}
          <span className="text-[10px] text-purple-300/70 italic">
            Cohortes · Messages · Scénarios · Comparateur · Débriefing
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-purple-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="p-4 border-t border-purple-500/30">
          <FormateurPhase3Panel apprentices={apprentices} />
        </div>
      )}
    </section>
  );
}

